import React, {useState} from "react";
import {DEFAULT_LEFT, SEARCH_MODEL} from "../../../../components/table/Model/TableModel";
import {SimpleSearchWithId} from "../../../../reduxToolkit/Search/SearchApi";
import TableFunction from "../../../../components/table/TableFunction";
import {useDispatch, useSelector} from "react-redux";
import {TABLES} from "../../../../Apis/Tables";
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile";
import {status, STATUS_MODEL} from "../../../../enums/statusEnum";
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem";
import {
    DeleteIconsOptions,
    DetailsIconsOptions
} from "../../../../components/buttons/IconButtons";
import {permissionObj} from "../../../../Helpers/permission";
import CustomChip from "../../../../components/CustomChip";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../enums/statusColorEnum";
import {VIEW_TYPE_LOGISTICS} from "../../../../enums/ViewType";
import {EMPTY_VALUES} from "../../../../Apis/functions";

/**
 * @fileoverview HistoryTabApp handles the HistoryTabApp tab for logistic requests.
 * It fetches and displays transport requests for a given supplier, and renders available actions
 * based on request status and user permissions.
 *
 * @module HistoryTabApp
 * @author Ferro Zapata
 * @email azapata@ibl.mx
 * @jobTitle Full Stack Developer
 */
/**
 * HistoryTabApp component
 *
 * Displays and manages the history transport requests tab with filtering, searching,
 * expandable row details, and dynamic option menus based on request status and permissions.
 *
 * @param {Object} props - Component props
 * @param {number} props.supplierId - Supplier user ID (null if not a supplier)
 * @param {Function} props.setShowDeleteModal - Sets visibility for the delete modal
 * @param {Function} props.setShowDetails - Sets visibility for the details modal
 * @param {Function} props.setRow - Sets the currently selected row
 * @param {boolean} props.modified - Flag to re-fetch data
 * @param {Function} props.setModified - Sets the modified flag
 *
 * @returns {Object} An object with state variables and handler functions for table and UI rendering
 */


export const HistoryTabApp =({
                                 supplierId,
                                 setShowDeleteModal,
                                 setShowDetails,
                                 setRow,
                                 modified,
                                 setModified,
                              }) => {
    const dispatch = useDispatch();

    const [selectedList, setSelectedList] = useState([]);
    const [searchElement, setSearchElement] = useState('');
    const [searchResults, setSearchResults] = useState('');
    const [bodyForExport, setBodyForExport] = useState();
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const searchId = 'txt_hta_search_transport_request_history';
    const option = TABLES.LOGISTICREQUEST;
    const model = TABLES.LOGISTICREQUEST;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);

    /**
     * Renders action options in a dropdown based on the status and permissions.
     * @param {Object} row - Row data
     * @returns {JSX.Element} The rendered menu
     */
    const renderOptionsMenu = (row) => {
        return (
            <>
                <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle}
                                     menuId={`menu-${row.id}`} isAccordion={true}>
                    {supplierId ?
                        <>
                            {[STATUS_MODEL.DELIVERED, STATUS_MODEL.CANCELED, STATUS_MODEL.REJECTED].includes(row.statusId) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('details')}
                                    click={() => {
                                        setShowDetails(true);
                                        setRow(row)
                                    }}
                                    icon={<DetailsIconsOptions/>}
                                />)
                            }
                        </> :
                        <>
                            {[STATUS_MODEL.DELIVERED, STATUS_MODEL.CANCELED, STATUS_MODEL.REJECTED].includes(row.statusId) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('details')}
                                    click={() => {
                                        setShowDetails(true);
                                        setRow(row)
                                    }}
                                    icon={<DetailsIconsOptions/>}
                                />)
                            }
                            {((DEV || permission?.includes(permissionObj?.WEB_SERVICE_TYPE_DELETE))
                                && [STATUS_MODEL.REQUESTED, STATUS_MODEL.ASSIGNED, STATUS_MODEL.DELIVERED,
                                    STATUS_MODEL.CANCELED, STATUS_MODEL.REJECTED].includes(row.statusId)) && (
                                <DropDownMenuItem
                                    index={row.id}
                                    handleClose={handleClose}
                                    name={t('delete')}
                                    click={() => {
                                        setSelectedList([row.id])
                                        setShowDeleteModal(true);
                                    }}
                                    icon={<DeleteIconsOptions/>}
                                    fontColor='font-color-error-600'
                                    iconColor={"icon-color-error-code-600"}
                                />
                            )}
                        </>
                    }
                </DropDownMenuProfile>
            </>
        )
    };

    /**
     * Defines table columns including custom status renderer.
     * @type {Array<Object>}
     */
    const columns = [
        { field: 'item', label: 'item', fixed: true, left: DEFAULT_LEFT },
        { field: 'originCenterName', label: 'origin_center_name' },
        { field: 'destinationCenterName', label: 'destination_center_name' },
        { field: 'departureAt', label: 'pickup_date' },
        { field: 'deliveryAt', label: 'delivery_date' },
        {
            field: 'statusId', label: 'status',
            render: (value) => {
                return (
                    <CustomChip
                        background={statusColor[value]}
                        fontColor={statusFontColor[value]}
                        borderColor={statusBorderColor[value]}
                        legend={t(status[value]) || EMPTY_VALUES.DEFAULT} variant={'large'}
                    />
                );
            }
        },
    ];

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
                page,
                size: rowsPerPage,
                model,
                userId: supplierId,
                viewType: VIEW_TYPE_LOGISTICS.HISTORY,
                key: searchElement
        })

        const action = SimpleSearchWithId

        setBodyForExport(body);
        dispatch(action({body})).then(
            ({payload: {data: {data, success}}}) => {
                if (success === true) {
                    setSearchResults(data);
                }
            },
        );
    }

    const {
        t,
        handleCheckboxChange,
        handleDeleteAll,
        handleClose,
        handleRowExpand,
        handleMenuToggle,
        pagination,
        openMenu,
        page,
        rowsPerPage
    } = TableFunction({
        selectedList,
        setSelectedList: setSelectedList,
        data: searchResults,
        labelRowsPerPage: 'transport_request_per_page',
        hasContainer : true,
        fixedPagination : false,

        searchElement,//New
        getData,//New
        modified,//New
        setModified//New
    });

    return {
        searchElement,
        setSearchElement,
        setBodyForExport,
        setSearchResults,
        option,
        page,
        rowsPerPage,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        searchResults,
        selectedList,
        searchId,
        columns,
        handleCheckboxChange,
        pagination,
        handleDeleteAll,
        renderOptionsMenu,
        handleRowExpand,
        t
    }
}


